import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Upload, CheckCircle, Send } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    studio: "", service: "", message: "",
    privacy: false,
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) { setError("Bitte stimme der Datenschutzerklärung zu."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data.success) setSuccess(true);
    } catch {
      setError("Fehler beim Senden. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-[#5A4A1A] text-[#F0F0F0] py-3 text-sm font-outfit placeholder-[#888888]/60 focus:outline-none focus:border-[#C9A84C] transition-colors duration-300";
  const selectClass = `${inputClass} appearance-none cursor-pointer`;

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="section-label">Kontakt</span>
          <h2 className="section-heading">Dein Termin.<br />Unser Versprechen.</h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* LEFT – Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {success ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <CheckCircle size={56} className="text-[#C9A84C] mb-6" strokeWidth={1.5} />
                <h3 className="font-cormorant text-4xl italic text-[#F0F0F0] mb-3">
                  Nachricht erhalten!
                </h3>
                <p className="text-[#888888] font-outfit">
                  Wir melden uns so schnell wie möglich bei dir.
                </p>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="space-y-7"
              >
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">Name *</label>
                    <input
                      type="text" name="name" required value={form.name}
                      onChange={handleChange} placeholder="Dein Name"
                      className={inputClass} data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">Telefon</label>
                    <input
                      type="tel" name="phone" value={form.phone}
                      onChange={handleChange} placeholder="+49 000 0000000"
                      className={inputClass} data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">E-Mail *</label>
                  <input
                    type="email" name="email" required value={form.email}
                    onChange={handleChange} placeholder="deine@email.de"
                    className={inputClass} data-testid="contact-email"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">Studio</label>
                    <select name="studio" value={form.studio} onChange={handleChange} className={selectClass} data-testid="contact-studio">
                      <option value="">Bitte wählen</option>
                      <option value="grevenbroich">Grevenbroich</option>
                      <option value="leverkusen">Leverkusen</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">Service</label>
                    <select name="service" value={form.service} onChange={handleChange} className={selectClass} data-testid="contact-service">
                      <option value="">Bitte wählen</option>
                      <option value="tattoo">Tattoo</option>
                      <option value="piercing">Piercing</option>
                      <option value="beratung">Beratung</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">
                    Deine Idee / Referenz (optional)
                  </label>
                  <label
                    data-testid="contact-file-upload"
                    className="flex items-center gap-3 border-b border-[#5A4A1A] hover:border-[#C9A84C] py-3 cursor-pointer transition-colors"
                  >
                    <Upload size={16} className="text-[#C9A84C] flex-shrink-0" />
                    <span className="text-sm font-outfit text-[#888888]">
                      {file ? file.name : "Bild hochladen (JPG, PNG)"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>

                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">Nachricht *</label>
                  <textarea
                    name="message" required value={form.message} onChange={handleChange}
                    rows={4} placeholder="Beschreibe dein Wunschmotiv, Größe, Platzierung..."
                    className={`${inputClass} resize-none`} data-testid="contact-message"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className={`mt-0.5 w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-colors ${form.privacy ? "border-[#C9A84C] bg-[#C9A84C]/20" : "border-[#5A4A1A]"}`}>
                    {form.privacy && <div className="w-2 h-2 bg-[#C9A84C]" />}
                  </div>
                  <input
                    type="checkbox" name="privacy" checked={form.privacy}
                    onChange={handleChange} className="hidden" data-testid="contact-privacy"
                  />
                  <span className="text-[#888888] text-xs font-outfit leading-relaxed">
                    Ich stimme der{" "}
                    <span className="text-[#C9A84C]">Datenschutzerklärung</span>{" "}
                    zu und bin einverstanden, dass meine Daten zur Bearbeitung meiner
                    Anfrage verwendet werden.
                  </span>
                </label>

                {error && (
                  <p className="text-red-400 text-sm font-outfit" data-testid="contact-error">{error}</p>
                )}

                <button
                  type="submit" disabled={loading}
                  data-testid="contact-submit"
                  className="gold-btn-solid flex items-center gap-3 disabled:opacity-50"
                >
                  {loading ? (
                    <span className="text-xs tracking-[0.2em] font-outfit">Wird gesendet...</span>
                  ) : (
                    <>
                      <Send size={15} />
                      <span className="text-xs tracking-[0.2em] font-outfit">Anfrage absenden</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* RIGHT – Addresses */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-8"
          >
            <p className="text-[#888888] text-sm font-outfit leading-relaxed border-l-2 border-[#C9A84C]/40 pl-4">
              Kostenlose Beratung inkl. Entwurf bei verbindlicher Terminvereinbarung.
            </p>

            {/* Studio Cards */}
            {[
              {
                name: "Grevenbroich", badge: "Stammhaus",
                street: "Kölner Straße 39", city: "41515 Grevenbroich",
                phone: "0178 4685450",
                hours: "Mo–Fr 11:00–19:00 · Sa 11:00–17:00",
                maps: "https://maps.google.com/?q=Kölner+Straße+39,+41515+Grevenbroich",
              },
              {
                name: "Leverkusen", badge: "Neu · Mai 2026",
                street: "Kölner Straße 28", city: "51379 Leverkusen (Opladen)",
                phone: "0176 66124487",
                hours: "Öffnungszeiten folgen",
                maps: "https://maps.google.com/?q=Kölner+Straße+28,+51379+Leverkusen",
              },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`contact-studio-${s.name.toLowerCase()}`}
                className="bg-[#141414] border border-[#5A4A1A]/30 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-cormorant text-xl text-[#F0F0F0] italic">{s.name}</h3>
                  <span className="text-[#C9A84C] text-xs tracking-[0.1em] font-outfit">{s.badge}</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin size={14} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#F0F0F0] text-sm">{s.street}</p>
                      <p className="text-[#888888] text-sm">{s.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={14} className="text-[#C9A84C] flex-shrink-0" />
                    <a href={`tel:${s.phone.replace(/\s/g, "")}`} className="text-[#888888] text-sm hover:text-[#C9A84C] transition-colors">{s.phone}</a>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={14} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                    <p className="text-[#888888] text-sm">{s.hours}</p>
                  </div>
                </div>
                <a
                  href={s.maps}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-maps-${s.name.toLowerCase()}`}
                  className="ghost-btn text-xs py-2 px-4 mt-5 inline-block"
                >
                  Route planen
                </a>
              </div>
            ))}

            <div className="bg-[#141414] border border-[#5A4A1A]/30 p-6">
              <p className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-3">E-Mail</p>
              <a href="mailto:royal.tattoo.downtown@gmail.com" className="text-[#F0F0F0] text-sm hover:text-[#C9A84C] transition-colors font-outfit">
                royal.tattoo.downtown@gmail.com
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
